import { useEffect, useMemo, useRef } from 'react';
import { useAtom } from 'jotai';
import { useCart } from '@/store/quick-cart/cart.context';
import { useCartVerify } from '../framework/rest/pbm';
import type { CartVerifyItemResponse } from '@/types';
import {
  buildCartVerifyPayload,
  calculatePBMComboSavings,
  getInterplayersCartSignature,
  getInterplayersPBMItems,
  getPBMBaseUnitPrice,
  shouldVerifyPBMCombo,
} from '@/lib/utils/pbm';
import { pbmComboVerifyAtom, type PbmComboVerifyState } from '@/store/pbm-combo-verify-atom';

const VERIFY_DEBOUNCE_MS = 500;

/** Survives cart sidebar unmount so we do not re-verify the same cart on every open. */
let lastVerifiedSignatureCache: string | null = null;

export function clearPbmVerifyCache() {
  lastVerifiedSignatureCache = null;
}

function syncComboStateFromCart(
  items: ReturnType<typeof useCart>['items'],
  setComboState: (update: PbmComboVerifyState) => void,
  status: PbmComboVerifyState['status'] = 'idle',
) {
  const interplayersItems = getInterplayersPBMItems(items);
  const savingsTotal = calculatePBMComboSavings(items);
  const discountedItemCount = interplayersItems.filter(
    (item) => item.pbm_info?.combo_applied,
  ).length;

  setComboState({
    status:
      status === 'idle' && savingsTotal > 0 && discountedItemCount > 0
        ? 'applied'
        : status,
    savingsTotal,
    discountedItemCount,
  });
}

/**
 * Runs Interplayers /cart-verify when the cart sidebar is open.
 * Verifies at most once per cart signature (sku + qty + cpf) until the cart changes.
 */
export function usePbmCartVerify() {
  const { items, applyPbmVerifiedPrices, revertInterplayersPbmToCatalog } = useCart();
  const { mutateAsync } = useCartVerify();
  const [, setComboState] = useAtom(pbmComboVerifyAtom);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const inFlightSignatureRef = useRef<string | null>(null);
  const mutateAsyncRef = useRef(mutateAsync);
  const itemsRef = useRef(items);

  mutateAsyncRef.current = mutateAsync;
  itemsRef.current = items;

  const signature = useMemo(
    () => getInterplayersCartSignature(items),
    [items],
  );

  useEffect(() => {
    const currentItems = itemsRef.current;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (!shouldVerifyPBMCombo(currentItems)) {
      inFlightSignatureRef.current = null;
      lastVerifiedSignatureCache = null;
      setComboState({
        status: 'idle',
        savingsTotal: 0,
        discountedItemCount: 0,
      });
      return;
    }

    const payload = buildCartVerifyPayload(currentItems);
    if (!payload) {
      setComboState({
        status: 'idle',
        savingsTotal: 0,
        discountedItemCount: 0,
      });
      return;
    }

    if (signature === lastVerifiedSignatureCache) {
      syncComboStateFromCart(currentItems, setComboState);
      return;
    }

    if (signature === inFlightSignatureRef.current) {
      return;
    }

    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;

      const currentItems = itemsRef.current;
      const currentPayload = buildCartVerifyPayload(currentItems);
      const currentSignature = getInterplayersCartSignature(currentItems);

      if (!currentPayload || !shouldVerifyPBMCombo(currentItems)) {
        return;
      }

      if (currentSignature === lastVerifiedSignatureCache) {
        syncComboStateFromCart(currentItems, setComboState);
        return;
      }

      if (currentSignature === inFlightSignatureRef.current) {
        return;
      }

      const requestId = ++requestIdRef.current;
      inFlightSignatureRef.current = currentSignature;
      setComboState((prev) => ({ ...prev, status: 'validating' }));

      void mutateAsyncRef
        .current(currentPayload)
        .then((verifiedPrices: CartVerifyItemResponse[]) => {
          if (requestId !== requestIdRef.current) {
            return;
          }

          if (getInterplayersCartSignature(itemsRef.current) !== currentSignature) {
            inFlightSignatureRef.current = null;
            return;
          }

          inFlightSignatureRef.current = null;
          lastVerifiedSignatureCache = currentSignature;

          applyPbmVerifiedPrices(verifiedPrices);

          const interplayersItems = getInterplayersPBMItems(currentItems);
          const savingsTotal = verifiedPrices.reduce((total: number, entry) => {
            const item = interplayersItems.find((line) => line.sku === entry.sku);
            if (!item) return total;
            const basePrice = getPBMBaseUnitPrice(item);
            const savings = (basePrice - entry.sale_price) * (item.quantity ?? 1);
            return savings > 0 ? total + savings : total;
          }, 0);

          const discountedItemCount = verifiedPrices.filter((entry) => {
            const item = interplayersItems.find((line) => line.sku === entry.sku);
            if (!item) return false;
            return entry.sale_price < getPBMBaseUnitPrice(item) - 0.001;
          }).length;

          setComboState({
            status: savingsTotal > 0 ? 'applied' : 'idle',
            savingsTotal,
            discountedItemCount,
          });
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) {
            return;
          }

          if (getInterplayersCartSignature(itemsRef.current) !== currentSignature) {
            inFlightSignatureRef.current = null;
            return;
          }

          inFlightSignatureRef.current = null;

          revertInterplayersPbmToCatalog();
          setComboState({
            status: 'error',
            savingsTotal: 0,
            discountedItemCount: 0,
          });
        });
    }, VERIFY_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [
    applyPbmVerifiedPrices,
    revertInterplayersPbmToCatalog,
    setComboState,
    signature,
  ]);
}
